import HistoryIcon from '@mui/icons-material/History';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useState } from 'react';

import { PayOnlineUrlHistoryEntry, getCustomInvoiceUrlHistory } from '../api';

type Props = {
  value: string;
  onChange: (value: string) => void;
  structureId: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

export const PayOnlineUrlField = ({
  value,
  onChange,
  structureId,
  label = 'Pay Online URL',
  placeholder = 'https://example.com/pay',
  helperText = 'Link for tenants to pay online (optional)',
  size,
  disabled,
  fullWidth = true,
  sx,
}: Props): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [history, setHistory] = useState<PayOnlineUrlHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Re-fetch every time the menu opens so the picker reflects URLs used after
    // the dialog was first opened (e.g. a teammate generated invoices elsewhere).
    setLoading(true);
    getCustomInvoiceUrlHistory(structureId)
      .then((res) => setHistory(res.urls))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  };

  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (url: string) => {
    onChange(url);
    closeMenu();
  };

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        helperText={helperText}
        size={size}
        disabled={disabled}
        sx={sx}
        autoComplete="off"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Previously used URLs">
                <span>
                  <IconButton
                    aria-label="show URL history"
                    onClick={openMenu}
                    disabled={disabled}
                    size="small"
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
        {loading && (
          <MenuItem disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading…
          </MenuItem>
        )}
        {!loading && history !== null && history.length === 0 && (
          <MenuItem disabled>No previously used URLs</MenuItem>
        )}
        {!loading &&
          history?.map((entry) => (
            <MenuItem key={entry.url} onClick={() => handleSelect(entry.url)}>
              {entry.url}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};
